/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download, Trash2, ShieldCheck, Clock, AlertTriangle } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { authAPI } from '../api/auth';
import { privacyAPI } from '../api/privacy';
import { fmtData } from '../utils/date';
import { useImpostazioni } from '../hooks/useImpostazioni';

const ACCENT = '#3B82F6';

export default function PrivacyAccount() {
  const impostazioni = useImpostazioni();
  const qc = useQueryClient();
  const [msg, setMsg] = useState('');
  const [scaricando, setScaricando] = useState(false);

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => authAPI.me() });
  const { data: richiestaCancellazione } = useQuery({
    queryKey: ['richiesta-cancellazione'],
    queryFn: () => privacyAPI.miaRichiestaCancellazione(),
  });

  const cancellazioneMut = useMutation({
    mutationFn: () => privacyAPI.richiediCancellazione(),
    onSuccess: () => {
      setMsg('');
      qc.invalidateQueries({ queryKey: ['richiesta-cancellazione'] });
    },
    onError: (e) => setMsg(e.response?.data?.error || 'Errore nella richiesta'),
  });

  const esportaDati = async () => {
    setMsg('');
    setScaricando(true);
    try {
      const dati = await privacyAPI.esportaDati();
      const blob = new Blob([JSON.stringify(dati, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // il nome del file segue quello dell'installazione, non un'azienda fissa
      const base = (impostazioni.app_nome || 'dati').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      a.download = `${base}-dati-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Errore durante l\'esportazione dei dati');
    } finally {
      setScaricando(false);
    }
  };

  const richiedi = () => {
    if (window.confirm('Confermi di voler richiedere la cancellazione del tuo account e dei tuoi dati personali? La richiesta verrà valutata dal nostro team.')) {
      cancellazioneMut.mutate();
    }
  };

  const inAttesa = richiestaCancellazione?.stato === 'in_attesa';

  return (
    <ClienteLayout title="Il mio account e la privacy">
      <div style={styles.card}>
        <div style={styles.sectionHead}>
          <ShieldCheck size={20} color={ACCENT} />
          <h3 style={styles.sectionTitle}>Consenso privacy</h3>
        </div>
        {me?.consenso_privacy_accettato ? (
          <p style={styles.text}>
            Hai accettato l'<Link to="/privacy" target="_blank" style={styles.link}>Informativa Privacy</Link>
            {me?.consenso_privacy_data && <> il {fmtData(me.consenso_privacy_data)}</>}.
          </p>
        ) : (
          <p style={styles.text}>Stato del consenso non disponibile. Consulta l'<Link to="/privacy" target="_blank" style={styles.link}>Informativa Privacy</Link>.</p>
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.sectionHead}>
          <Download size={20} color={ACCENT} />
          <h3 style={styles.sectionTitle}>Esporta i tuoi dati</h3>
        </div>
        <p style={styles.text}>Scarica una copia di tutti i dati che conserviamo su di te: profilo, interventi, prodotti installati.</p>
        <button onClick={esportaDati} disabled={scaricando} style={styles.btn}>
          {scaricando ? 'Preparazione...' : 'Scarica i miei dati (JSON)'}
        </button>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionHead}>
          <Trash2 size={20} color="#DC2626" />
          <h3 style={styles.sectionTitle}>Cancellazione account</h3>
        </div>
        <p style={styles.text}>
          Puoi richiedere la cancellazione del tuo account e dei tuoi dati personali. Gli interventi già fatturati potrebbero essere conservati in forma anonima per obblighi fiscali.
        </p>
        {inAttesa ? (
          <div style={styles.pendingBox}>
            <Clock size={16} /> Richiesta inviata il {fmtData(richiestaCancellazione.created_at)}, in attesa di revisione.
          </div>
        ) : richiestaCancellazione?.stato === 'rifiutata' ? (
          <>
            <div style={styles.rejectedBox}>
              <AlertTriangle size={16} /> Ultima richiesta rifiutata{richiestaCancellazione.note ? `: ${richiestaCancellazione.note}` : '.'}
            </div>
            <button onClick={richiedi} disabled={cancellazioneMut.isPending} style={styles.btnDanger}>
              {cancellazioneMut.isPending ? 'Invio...' : 'Richiedi di nuovo la cancellazione'}
            </button>
          </>
        ) : (
          <button onClick={richiedi} disabled={cancellazioneMut.isPending} style={styles.btnDanger}>
            {cancellazioneMut.isPending ? 'Invio...' : 'Richiedi cancellazione account'}
          </button>
        )}
        {msg && <div style={styles.errorBox}>{msg}</div>}
      </div>
    </ClienteLayout>
  );
}

const styles = {
  card: { backgroundColor: 'white', padding: '1.25rem 1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.25rem' },
  sectionHead: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' },
  sectionTitle: { margin: 0, fontSize: '1.02rem', fontWeight: 700 },
  text: { color: '#4B5563', fontSize: '0.92rem', lineHeight: 1.5, margin: '0 0 0.9rem' },
  link: { color: ACCENT, fontWeight: 600 },
  btn: { padding: '0.65rem 1.2rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' },
  btnDanger: { padding: '0.65rem 1.2rem', backgroundColor: 'white', color: '#DC2626', border: '1px solid #DC2626', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' },
  pendingBox: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#FEF3C7', color: '#92400E', padding: '0.7rem 0.9rem', borderRadius: '8px', fontSize: '0.88rem' },
  rejectedBox: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.7rem 0.9rem', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '0.75rem' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', marginTop: '0.75rem' },
};
